import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HERO = "/assets/static-hero.jpg";

function Shell({ title, children }) {
  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 -z-10"><img src={HERO} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-[#FDFBF7]/90" /></div>
        <div className="max-w-3xl mx-auto px-5 py-14 text-center"><h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A]">{title}</h1></div>
      </section>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 prose-p:leading-relaxed">{children}</div>
    </div>
  );
}
const P = ({ children }) => <p className="text-[#475569] leading-relaxed mb-4">{children}</p>;
const H = ({ children }) => <h2 className="font-serif-display text-2xl text-[#0F172A] mt-8 mb-3">{children}</h2>;

export function About() {
  return (
    <Shell title="About I Need a Proverb">
      <P>I Need a Proverb exists for one reason: to help you find biblical wisdom for the emotion, struggle, relationship problem, temptation, or decision you are facing — quickly and gently.</P>
      <P>Rather than a generic Bible search, this is an immediate wisdom finder. You tell us what you're dealing with, and we draw carefully matched verses from the Book of Proverbs (World English Bible, public domain), each paired with a plain-English explanation, a practical next step, and a short prayer.</P>
      <H>Our promise about Scripture</H>
      <P>Every verse you see comes directly from our reviewed Scripture database. We never allow AI to invent verses or references. AI is used only to help match your situation to approved categories — never to write Scripture or present commentary as the Bible.</P>
      <H>Connected with LifeGuide</H>
      <P>I Need a Proverb works together with LifeGuide to help you go beyond finding the right Scripture. When you're ready to go deeper, you can continue into LifeGuide for personalized guidance, decision support, journaling, and spiritual growth.</P>
    </Shell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/email-capture", { email: form.email, source: "contact" }); toast.success("Thank you — we've received your message."); setForm({ name: "", email: "", message: "" }); }
    catch { toast.error("Could not send."); }
  };
  return (
    <Shell title="Contact">
      <P>Questions, feedback, or a story to share? We'd love to hear from you.</P>
      <form onSubmit={submit} className="space-y-4 bg-white border border-[#E2E8F0] rounded-2xl p-7 not-prose">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2.5 focus:outline-none focus:border-[#2C4C3B]" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2.5 focus:outline-none focus:border-[#2C4C3B]" />
        <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message" className="w-full rounded-lg border border-[#E2E8F0] px-4 py-3 resize-none focus:outline-none focus:border-[#2C4C3B]" />
        <button data-testid="contact-submit-btn" className="px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-semibold">Send message</button>
      </form>
    </Shell>
  );
}

export function ScriptureInfo() {
  return (
    <Shell title="Scripture & Translation">
      <P>All Scripture on I Need a Proverb is taken from the <strong>World English Bible (WEB)</strong>, a modern public-domain English translation. Because it is public domain, we are free to display, print, and share it without restriction.</P>
      <H>The Book of Proverbs</H>
      <P>Our initial content source is the Book of Proverbs — a collection of practical wisdom for everyday life, relationships, work, and the heart. We are building toward a complete, categorized database of every verse in Proverbs.</P>
      <H>Reviewed for faithfulness</H>
      <P>Verse text is stored exactly as translated. Explanations, applications, and prayers are clearly labeled and are our own commentary — never presented as Scripture.</P>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <P>We respect your privacy. This policy explains how we handle your information.</P>
      <H>What we store</H>
      <P>Account details (name, email), your saved Wisdom Paths, favorites, private journal entries, search history, and email subscriptions. If you choose to receive text messages, we also store the mobile number and consent you provide.</P>
      <H>Your private journal</H>
      <P>Your journal entries are private and are never visible to other users.</P>
      <H>SMS / text messaging</H>
      <P>Mobile phone numbers and SMS consent are <strong>never shared, sold, rented, or disclosed to third parties for marketing purposes</strong>. Your mobile information is used only to deliver the text messages you have expressly opted in to receive.</P>
      <P>Message frequency varies. Message and data rates may apply. You can reply <strong>STOP</strong> at any time to cancel text messages, or reply <strong>HELP</strong> for help.</P>
      <H>Your control</H>
      <P>You can delete your account and all stored information at any time from your <a href="/account" className="text-[#2C4C3B] underline">Account</a> page.</P>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service">
      <P>By using I Need a Proverb you agree to use it for personal spiritual encouragement and study.</P>
      <H>Not a substitute for professional help</H>
      <P>I Need a Proverb provides Scripture-based educational and spiritual-growth content. It is not a substitute for professional medical, mental-health, legal, financial, emergency, or pastoral assistance. In an emergency, contact local emergency services immediately.</P>
      <H>SMS / text messaging terms</H>
      <P>You will only receive recurring automated text messages from I Need a Proverb after you enter your own mobile number and check the consent box, which is unchecked by default. Providing your number and checking that box is your express consent to receive these messages. <strong>Consent is not a condition of any purchase.</strong></P>
      <P>Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> at any time to cancel, or reply <strong>HELP</strong> for help.</P>
      <P>Your mobile number and opt-in consent are <strong>not sold, rented, or shared with third parties for their marketing</strong>. See our <a href="/privacy" className="text-[#2C4C3B] underline">Privacy Policy</a> for details on how we handle your mobile information.</P>
      <H>Content</H>
      <P>Scripture is public domain (WEB). Explanations, applications, prayers, and guides are provided for encouragement and may be updated over time.</P>
    </Shell>
  );
}
